import { Banner, Code, Link, Paragraph, Stack, Strong } from '@customerio/pluma-components/react';
import { useRouter } from '@tanstack/react-router';
import { isGeoIpAttribute } from '../utils/item';

/**
 * What deleting one attribute is about to do.
 *
 * Two things are worth saying before anyone commits: the values go with it,
 * and the messages that read it will not be fixed for them. An attribute
 * Customer.io writes from a visitor's location gets a third: deleting it is
 * undone by the next event.
 */

type DeleteMessageProps = {
  workspaceId: number;
  name: string;
  /** What the workspace calls the people it holds, plural. */
  peoplePlural: string;
};

export function DeleteAttributeMessage({ workspaceId, name, peoplePlural }: DeleteMessageProps) {
  const router = useRouter();
  // The query is appended by hand: an attribute's name is customer-authored,
  // and `buildLocation` would write `2024` as the quoted string `"2024"`.
  const messageLibraryPath = router.buildLocation({
    to: '/workspaces/$workspaceId/message-library',
    params: { workspaceId: String(workspaceId) },
  }).href;
  const messageLibraryHref = `${messageLibraryPath}?${new URLSearchParams({ query: name }).toString()}`;

  return (
    <Stack gap="150">
      {isGeoIpAttribute(name) ? (
        <Banner
          variant="caution"
          title="Auto-generated attribute"
          description={`This attribute is automatically generated by Customer.io based on IP geolocation. If geolocation is enabled in your workspace, this data may be regenerated when ${peoplePlural} are updated. You can manage geolocation settings in Workspace settings > Time Zone & Geolocation Settings.`}
        />
      ) : null}
      <Paragraph>
        This action is <Strong>permanent</Strong> and <Strong>cannot be undone</Strong>. Deleting this attribute will
        remove <Code>{name}</Code> from all {peoplePlural}.
      </Paragraph>
      {/* A div, not a paragraph: the link below carries an icon, which Pluma
          renders as a box, and a box cannot sit inside a `p`. */}
      <Paragraph as="div">
        Note, references to this attribute in messages will not be automatically removed, which may cause those messages
        to fail. To avoid issues, review and update your messages in the{' '}
        <Link isUnderlined href={messageLibraryHref} isExternal icon="exit" iconPosition="trailing">
          message library
        </Link>
        .
      </Paragraph>
      <Paragraph>To confirm deletion, type the attribute name below.</Paragraph>
    </Stack>
  );
}
